import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ParcelamentoAPI } from '../types/parcelling.types';
import { NUTRIENTS, NUTRIENT_LABELS, type Nutrient } from '../constants/nutrients.constants';
import { HARVEST_PHASE_LABELS, type HarvestPhase } from '../constants/phases.constants';
import { CULTURE_LABELS } from '../constants/cultures.constants';
import { CROP_STAGE_LABELS } from '../constants/cropStages.constants';
import { getMonthLabel } from './parcellingFormatters';
import { fromFixedParcelPayload } from './fixedParcel';

const buildTableData = (parcelling: ParcelamentoAPI) => {
	const headers = ['Mês', 'Fase', ...NUTRIENTS.map((n) => NUTRIENT_LABELS[n])];
	const grid = fromFixedParcelPayload(parcelling.fixedParcel);

	const rows: (string | number)[][] = [];
	const totals: number[] = new Array(NUTRIENTS.length).fill(0);

	for (let i = 0; i < 12; i++) {
		const phase = grid.phase[i] as HarvestPhase;
		const row: (string | number)[] = [
			getMonthLabel(i),
			phase ? (HARVEST_PHASE_LABELS[phase] ?? '') : '',
		];
		NUTRIENTS.forEach((nutrient, ni) => {
			const val = grid[nutrient as Nutrient][i];
			const numVal = Number.isNaN(val) ? 0 : val;
			row.push(numVal);
			totals[ni] += numVal;
		});
		rows.push(row);
	}

	const totalRow: (string | number)[] = ['Total', '', ...totals.map((t) => Math.round(t * 10) / 10)];
	rows.push(totalRow);

	return { headers, rows };
};

const getInfoRows = (parcelling: ParcelamentoAPI) => [
	['Identificação', parcelling.name],
	['Cultura', CULTURE_LABELS[parcelling.culture] || parcelling.culture],
	['Estágio', CROP_STAGE_LABELS[parcelling.cropStage] || parcelling.cropStage],
	['Observação', parcelling.note || '-'],
];

export const exportToExcel = (parcelling: ParcelamentoAPI) => {
	const { headers, rows } = buildTableData(parcelling);
	const info = getInfoRows(parcelling);

	const wsData: (string | number)[][] = [
		...info,
		[],
		headers,
		...rows,
	];

	const ws = XLSX.utils.aoa_to_sheet(wsData);

	const colWidths = headers.map((_h, i) => {
		if (i === 0) return { wch: 14 };
		if (i === 1) return { wch: 16 };
		return { wch: 8 };
	});
	ws['!cols'] = colWidths;

	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, ws, 'Parcelamento');
	XLSX.writeFile(wb, `${parcelling.name || 'parcelamento'}.xlsx`);
};

export const exportToPdf = (parcelling: ParcelamentoAPI) => {
	const { headers, rows } = buildTableData(parcelling);
	const info = getInfoRows(parcelling);

	const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

	doc.setFontSize(16);
	doc.text('Parcelamento', 14, 15);

	doc.setFontSize(10);
	let y = 24;
	for (const [label, value] of info) {
		doc.setFont('helvetica', 'bold');
		doc.text(`${label}: `, 14, y);
		const labelWidth = doc.getTextWidth(`${label}: `);
		doc.setFont('helvetica', 'normal');
		doc.text(String(value), 14 + labelWidth, y);
		y += 6;
	}

	autoTable(doc, {
		startY: y + 4,
		head: [headers],
		body: rows.map((row) => row.map(String)),
		styles: {
			fontSize: 8,
			cellPadding: 2,
		},
		headStyles: {
			fillColor: [90, 106, 207],
			textColor: 255,
			fontStyle: 'bold',
		},
		alternateRowStyles: {
			fillColor: [245, 245, 245],
		},
		didParseCell: (data) => {
			if (data.row.index === rows.length - 1) {
				data.cell.styles.fontStyle = 'bold';
				data.cell.styles.fillColor = [230, 230, 230];
			}
		},
	});

	doc.save(`${parcelling.name || 'parcelamento'}.pdf`);
};
